import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateTimeBR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  ArrowDownToLine, ArrowUpFromLine, TrendingUp, RotateCcw,
  AlertTriangle, ShieldCheck, Plus, Loader2, Trash2, History, Briefcase,
} from "lucide-react";
import { HeroKpi, AllocationBar } from "../components/HeroKpi";

const TYPE_LABELS = {
  aporte: { label: "Aporte", icon: ArrowDownToLine, color: "text-emerald-300", badge: "badge-aprovado" },
  retirada: { label: "Retirada (legado)", icon: ArrowUpFromLine, color: "text-red-300", badge: "badge-negado" },
  retirada_capital: { label: "Retirada de capital", icon: ArrowUpFromLine, color: "text-red-300", badge: "badge-negado" },
  retirada_lucro: { label: "Retirada de lucro", icon: ArrowUpFromLine, color: "text-red-400", badge: "badge-negado" },
  lucro_recebido: { label: "Lucro recebido", icon: TrendingUp, color: "text-emerald-300", badge: "badge-aprovado" },
  lucro_reinvestido: { label: "Lucro reinvestido", icon: RotateCcw, color: "text-[#D4AF37]", badge: "badge-contratado" },
  deficit: { label: "Déficit", icon: AlertTriangle, color: "text-red-300", badge: "badge-negado" },
  recomposicao_deficit: { label: "Recomposição déficit", icon: ShieldCheck, color: "text-blue-300", badge: "badge-novo" },
};

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="p-3 rounded-md border border-[#161616] bg-[#0a0a0a]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono-tab text-[14px] mt-1.5 leading-none ${accent || "text-[#D4D4D4]"}`}>{value}</div>
    </div>
  );
}

function DetailSection({ title, icon, children }) {
  return (
    <div className="fc-card p-4 sm:p-5 bg-[#080808] border-[#181818]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#737373] mb-3 flex items-center gap-2 font-medium">
        <span className="opacity-70">{icon}</span>{title}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {children}
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, op, accent, hint }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-baseline px-3 py-2 border-b border-[#141414] last:border-0">
      <div className="col-span-1 font-mono-tab text-[#737373] text-center">{op || ""}</div>
      <div className="col-span-7">
        <div className="text-[12.5px]">{label}</div>
        {hint && <div className="text-[10.5px] text-[#737373] mt-0.5">{hint}</div>}
      </div>
      <div className={`col-span-4 text-right font-mono-tab text-[13px] ${accent || ""}`}>{formatBRL(value)}</div>
    </div>
  );
}

export default function TreasuryPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [socios, setSocios] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ type: "aporte", amount: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [showLucroBreak, setShowLucroBreak] = useState(false);

  const loadSocios = async () => {
    try {
      const { data } = await api.get("/socios");
      setSocios(data);
      if (data.length > 0) setSelected(data[0].id);
    } catch (_) { toast.error("Erro ao carregar sócios"); }
    finally { setLoading(false); }
  };

  const loadTreasury = async (sid) => {
    if (!sid) return;
    try {
      const { data } = await api.get(`/socios/${sid}/treasury`);
      setData(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro ao carregar tesouraria"); }
  };

  useEffect(() => { loadSocios(); }, []);
  useEffect(() => { if (selected) loadTreasury(selected); }, [selected]);

  const lancar = async (e) => {
    e.preventDefault();
    if (!selected || !form.amount) return;
    setSaving(true);
    try {
      await api.post(`/socios/${selected}/ledger`, {
        type: form.type,
        amount: Number(form.amount),
        notes: form.notes,
      });
      toast.success("Lançamento registrado");
      setForm({ type: "aporte", amount: "", notes: "" });
      loadTreasury(selected);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao lançar");
    } finally { setSaving(false); }
  };

  const remover = async (entryId) => {
    if (!window.confirm("Excluir este lançamento? A ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/socios/${selected}/ledger/${entryId}`);
      toast.success("Lançamento excluído");
      loadTreasury(selected);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  if (loading) {
    return <Layout><div className="fc-card h-64 shimmer"/></Layout>;
  }

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Caixa</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Tesouraria dos Sócios</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Controle individual de aportes, retiradas, lucros, déficit e saldo operacional</p>
      </div>

      {/* Socio selector */}
      {isAdmin && socios.length > 1 && (
        <div className="fc-card p-4 mb-5 flex items-center gap-3 flex-wrap">
          <Briefcase size={16} className="text-[#D4AF37]"/>
          <span className="text-sm text-[#A3A3A3]">Sócio:</span>
          <div className="flex flex-wrap gap-2" data-testid="socio-selector">
            {socios.map(s => (
              <button
                key={s.id}
                onClick={()=>setSelected(s.id)}
                className={`px-3.5 py-1.5 rounded-md text-[12.5px] border transition-colors ${selected === s.id ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#262626] text-[#A3A3A3] hover:border-[#3a3a3a]"}`}
                data-testid={`socio-tab-${s.id}`}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {!data ? (
        <div className="fc-card p-12 text-center text-[#737373]">
          {socios.length === 0 ? "Nenhum sócio cadastrado." : "Selecione um sócio."}
        </div>
      ) : (
        <>
          {/* HERO KPIs — visão executiva no topo */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5" data-testid="treasury-hero">
            <HeroKpi label="Capital Investido Total" value={formatBRL(data.capital_investido_total)} variant="investido" testid="hero-investido"/>
            <HeroKpi label="Exposição Total" value={formatBRL(data.capital_na_rua)} variant="rua" testid="hero-exposicao"/>
            <HeroKpi label="Saldo Operacional" value={formatBRL(data.saldo_operacional)} variant="disponivel"
              principal
              valueColor={data.saldo_operacional < 0 ? "text-red-300" : undefined} testid="hero-saldo"/>
            <HeroKpi label="Lucro disponível p/ saque" value={formatBRL(data.lucro_disponivel_saque)} variant="lucro" dim={!(data.lucro_disponivel_saque > 0)} testid="hero-lucro-saque"/>
          </div>

          {/* Auditoria do cálculo de Lucro Disponível — clique para revelar */}
          <div className="mb-6 -mt-2">
            <button
              type="button"
              onClick={() => setShowLucroBreak(v => !v)}
              className="text-[11px] text-[#A3A3A3] hover:text-white flex items-center gap-1.5 px-3 py-1.5 rounded border border-[#1f1f1f] bg-[#0a0a0a] transition-colors"
              data-testid="btn-lucro-breakdown"
            >
              <span className="text-[#D4AF37]">🔍</span>
              {showLucroBreak ? "Ocultar" : "Auditar"} cálculo do Lucro disponível p/ saque
            </button>
            {showLucroBreak && (() => {
              const jurosBruto = data.juros_recebidos || 0;
              const jurosContrBruto = data.juros_recebidos_contratos_bruto || 0;
              const jurosParcSocio = data.juros_recebidos_parcelados_socio || 0;
              const ledgerLucroRecebido = data.total_lucros_recebidos_contratos || 0;
              const reinvestido = data.total_lucros_reinvestidos || 0;
              const computed = jurosBruto - ledgerLucroRecebido - reinvestido;
              return (
                <div className="fc-card mt-3 p-4 sm:p-5" data-testid="lucro-breakdown-panel">
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold mb-3">
                    Fórmula atual — Lucro disponível p/ saque
                  </div>
                  <div className="rounded-md border border-[#1f1f1f] bg-[#080808]">
                    <BreakdownRow label="Juros recebidos (sócio)" value={jurosBruto} accent="text-emerald-300"
                      hint={`= 50% × R$ ${jurosContrBruto.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} (contratos bruto) + R$ ${jurosParcSocio.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} (parcelados sócio)`}/>
                    <BreakdownRow label="Lucro recebido (ledger)" value={ledgerLucroRecebido} op="−" accent="text-red-300"
                      hint="Soma de socio_ledger.amount WHERE type=lucro_recebido (admin registrou pagamento manual ao sócio)"/>
                    <BreakdownRow label="Lucro reinvestido (ledger)" value={reinvestido} op="−" accent="text-red-300"
                      hint="Soma de socio_ledger.amount WHERE type=lucro_reinvestido (sócio converteu lucro em capital)"/>
                    <div className="grid grid-cols-12 gap-2 items-baseline px-3 py-2.5 bg-[#0a0a0a] border-t-2 border-[#D4AF37]/30">
                      <div className="col-span-1 font-mono-tab text-[#D4AF37] text-center">=</div>
                      <div className="col-span-7 text-[13px] font-semibold text-white">Lucro disponível p/ saque (calculado)</div>
                      <div className="col-span-4 text-right font-mono-tab text-[15px] text-emerald-300 font-bold">{formatBRL(computed)}</div>
                    </div>
                  </div>
                  <div className="mt-3 text-[11.5px] text-[#A3A3A3] space-y-1.5">
                    <div><strong className="text-white">Interpretação:</strong> representa o lucro que <em>teoricamente</em> ainda não foi pago nem reinvestido pelo sócio. O cálculo subtrai do lucro auto-contabilizado (50% dos juros dos contratos + 100% do sócio nos parcelados) apenas os <strong className="text-[#D4AF37]">lançamentos manuais</strong> que o admin fez no ledger.</div>
                    <div className="text-amber-300/90"><strong>⚠ Possível distorção:</strong> Se algum lucro foi <strong>pago de fato</strong> ao sócio mas <strong>não foi registrado</strong> no ledger como <code>lucro_recebido</code>, o valor &ldquo;disponível p/ saque&rdquo; ficará <strong>inflado</strong> (o sistema vai achar que ainda há dinheiro a pagar).</div>
                    <div className="text-blue-300/90"><strong>ℹ Observação:</strong> a parte de juros dos parcelados (R$ {jurosParcSocio.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}) é contabilizada como &ldquo;ganha&rdquo; automaticamente, mas não é descontada como &ldquo;paga&rdquo;. Se o sócio já recebeu esse valor fora do ledger, ela aparece como disponível erroneamente.</div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Barra de alocação */}
          <div className="mb-8">
            <AllocationBar
              investido={data.capital_investido_total}
              naRua={data.capital_na_rua}
              disponivel={data.saldo_operacional}
              testid="treasury-allocation"
            />
          </div>

          {/* Detalhamentos agrupados */}
          <div className="space-y-4 mb-6">
            <DetailSection title="Exposição" icon="📊" accent="text-purple-300">
              <Kpi label="Capital na Rua (Contratos)" value={formatBRL(data.capital_na_rua_contratos)} accent="text-[#D4AF37]" testid="kpi-na-rua-contratos"/>
              <Kpi label="Parcelados e Acordos" value={formatBRL(data.capital_na_rua_parcelados)} accent="text-purple-300" testid="kpi-parcelados"/>
              <Kpi label="Juros a Receber" value={formatBRL(data.juros_a_receber)} accent="text-yellow-300" testid="kpi-juros-rec"/>
              <Kpi label="Juros Recebidos" value={formatBRL(data.juros_recebidos)} accent="text-emerald-300" testid="kpi-juros-recv"/>
            </DetailSection>

            <DetailSection title="Resultado Financeiro" icon="📈" accent="text-emerald-300">
              <Kpi label="Lucros Recebidos" value={formatBRL(data.total_lucros_recebidos)} accent="text-emerald-300" testid="kpi-lucros"/>
              <Kpi label="Lucros Reinvestidos" value={formatBRL(data.total_lucros_reinvestidos)} accent="text-[#D4AF37]" testid="kpi-lucros-reinv"/>
            </DetailSection>

            <DetailSection title="Movimentações" icon="💰" accent="text-blue-300">
              <Kpi label="Aportes" value={formatBRL(data.total_aportes)} accent="text-emerald-300" testid="kpi-aportes"/>
              <Kpi label="Retiradas" value={formatBRL(data.total_retiradas)} accent="text-red-300" testid="kpi-retiradas"/>
              <Kpi label="Capital Investido (base)" value={formatBRL(data.capital_invested_base)} testid="kpi-base"/>
            </DetailSection>

            <DetailSection title="Saúde Financeira" icon="⚠️" accent="text-amber-300">
              <Kpi label="Déficit" value={formatBRL(data.total_deficit)} accent={data.total_deficit > 0 ? "text-red-300" : ""} testid="kpi-deficit"/>
              <Kpi label="Recomposição Déficit" value={formatBRL(data.total_recomposicao_deficit)} accent="text-blue-300" testid="kpi-recomp"/>
              <Kpi label="Capital Disponível" value={formatBRL(data.capital_disponivel)} accent={data.capital_disponivel < 0 ? "text-red-300" : "text-emerald-300"} testid="kpi-disponivel"/>
            </DetailSection>
          </div>

          <div className="text-[11px] text-[#737373] mb-6 px-4 py-3 rounded bg-[#0a0a0a] border border-[#1f1f1f]">
            <strong className="text-[#A3A3A3]">Saldo operacional</strong> = Capital investido total + Lucros reinvestidos − Déficit + Recomposição − Exposição total (contratos + parcelados).
            <strong className="text-[#A3A3A3] ml-3">Lucro disponível p/ saque</strong> = Juros recebidos − Lucros recebidos − Lucros reinvestidos.
          </div>

          {/* New entry form (admin only) */}
          {isAdmin && (
            <form onSubmit={lancar} className="fc-card p-5 mb-6" data-testid="ledger-form">
              <h3 className="font-display text-base font-semibold mb-4 flex items-center gap-2">
                <Plus size={16} className="text-[#D4AF37]"/> Novo lançamento
              </h3>
              <div className="grid sm:grid-cols-4 gap-3">
                <div>
                  <label className="fc-label">Tipo</label>
                  <select required className="fc-input" value={form.type} onChange={e=>setForm({...form, type: e.target.value})} data-testid="ledger-type">
                    {Object.entries(TYPE_LABELS).map(([k,v]) => (<option key={k} value={k}>{v.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="fc-label">Valor (R$)</label>
                  <input required type="number" step="0.01" min="0.01" className="fc-input" value={form.amount} onChange={e=>setForm({...form, amount: e.target.value})} data-testid="ledger-amount"/>
                </div>
                <div className="sm:col-span-2">
                  <label className="fc-label">Observação</label>
                  <input className="fc-input" value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} data-testid="ledger-notes"/>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button type="submit" disabled={saving} className="fc-btn-gold flex items-center gap-2" data-testid="ledger-submit">
                  {saving && <Loader2 size={14} className="animate-spin"/>} Lançar
                </button>
              </div>
            </form>
          )}

          {/* History */}
          <div className="fc-card overflow-hidden" data-testid="treasury-history">
            <div className="p-5 border-b border-[#1f1f1f] flex items-center gap-2">
              <History size={16} className="text-[#D4AF37]"/>
              <h3 className="font-display text-base font-semibold tracking-tight">Histórico</h3>
              <span className="text-[12px] text-[#737373]">({data.ledger.length})</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                    <th className="py-3 px-4">Data</th>
                    <th className="py-3 px-4">Tipo</th>
                    <th className="py-3 px-4">Valor</th>
                    <th className="py-3 px-4">Observação</th>
                    <th className="py-3 px-4">Por</th>
                    {isAdmin && <th className="py-3 px-4 text-right pr-5"></th>}
                  </tr>
                </thead>
                <tbody>
                  {data.ledger.length === 0 && (
                    <tr><td colSpan={6} className="py-10 text-center text-[#737373]">Nenhum lançamento ainda.</td></tr>
                  )}
                  {data.ledger.map(e => {
                    const t = TYPE_LABELS[e.type] || { label: e.type, badge: "" };
                    const Icon = t.icon;
                    return (
                      <tr key={e.id} className="border-b border-[#161616]" data-testid={`ledger-row-${e.id}`}>
                        <td className="py-3 px-4 text-[#A3A3A3] whitespace-nowrap">{formatDateTimeBR(e.created_at)}</td>
                        <td className="py-3 px-4">
                          <span className={`badge-base ${t.badge}`}>
                            {Icon && <Icon size={11}/>} {t.label}
                          </span>
                        </td>
                        <td className={`py-3 px-4 font-mono-tab font-semibold ${t.color || ""}`}>{formatBRL(e.amount)}</td>
                        <td className="py-3 px-4 text-[#A3A3A3]">{e.notes || "—"}</td>
                        <td className="py-3 px-4 text-[11px] text-[#737373]">{e.created_by_name || e.created_by_email}</td>
                        {isAdmin && (
                          <td className="py-3 px-4 text-right pr-5">
                            <button onClick={()=>remover(e.id)} className="text-red-400/70 hover:text-red-300" title="Excluir" data-testid={`delete-${e.id}`}>
                              <Trash2 size={13}/>
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
